/**
 * Session Service - Manage short-lived access sessions
 *
 * Provides:
 * - Verify a session token via the backend
 * - Store session data in localStorage (shared across tabs)
 * - Check/set session state for protecting public pages
 */

const SESSION_STORAGE_KEY = 'session_token';
const SESSION_INFO_KEY = 'session_info';
const SESSION_SCOPE_KEY = 'session_scope';

/**
 * Verify a session token by calling the backend API.
 * @param {string} token - The session token to verify
 * @returns {Promise<{valid: boolean, session?: object, reason?: string}>}
 */
export async function verifySession(token) {
  if (!token) {
    return { valid: false, reason: 'Token is required' };
  }

  try {
    const response = await fetch(`/api/session-verify?token=${encodeURIComponent(token)}`);
    if (!response.ok) {
      return { valid: false, reason: 'Verification service unavailable' };
    }
    return await response.json();
  } catch (error) {
    console.error('[SessionService] Verification error:', error);
    return { valid: false, reason: 'Network error' };
  }
}

/**
 * Save session token to localStorage (persists across browser tabs).
 * Also stores scope data separately for easy access.
 * @param {string} token
 * @param {object} sessionInfo - Optional session metadata
 */
export function saveSession(token, sessionInfo = {}) {
  if (token) {
    localStorage.setItem(SESSION_STORAGE_KEY, token);
    localStorage.setItem(SESSION_INFO_KEY, JSON.stringify(sessionInfo));
    // Store scope separately for quick access
    if (sessionInfo && sessionInfo.scope) {
      localStorage.setItem(SESSION_SCOPE_KEY, JSON.stringify(sessionInfo.scope));
    } else {
      localStorage.removeItem(SESSION_SCOPE_KEY);
    }
  }
}

/**
 * Get the stored session token.
 * @returns {string|null}
 */
export function getSessionToken() {
  return localStorage.getItem(SESSION_STORAGE_KEY);
}

/**
 * Get stored session info.
 * @returns {object|null}
 */
export function getSessionInfo() {
  try {
    const info = localStorage.getItem(SESSION_INFO_KEY);
    return info ? JSON.parse(info) : null;
  } catch {
    return null;
  }
}

/**
 * Clear session data from localStorage.
 */
export function clearSession() {
  localStorage.removeItem(SESSION_STORAGE_KEY);
  localStorage.removeItem(SESSION_INFO_KEY);
  localStorage.removeItem(SESSION_SCOPE_KEY);
}

/**
 * Full session validation: check localStorage, then verify with backend.
 * @returns {Promise<{valid: boolean, session?: object, reason?: string}>}
 */
export async function validateCurrentSession() {
  const token = getSessionToken();
  if (!token) {
    return { valid: false, reason: 'No session token found' };
  }

  const result = await verifySession(token);
  if (result.valid) {
    // Update stored session info
    saveSession(token, result.session);
  } else {
    // Clear invalid session
    clearSession();
  }

  return result;
}

/**
 * Check if the session gate feature is globally enabled.
 * Calls the backend config API.
 * @returns {Promise<boolean>}
 */
export async function isSessionGateEnabled() {
  try {
    const response = await fetch('/api/session-config');
    if (!response.ok) {
      return true; // Default to enabled on error
    }
    const data = await response.json();
    return data.enabled !== false;
  } catch (error) {
    console.error('[SessionService] Config check error:', error);
    return true; // Default to enabled on error
  }
}

/**
 * Toggle the session gate feature on/off (admin only).
 * @param {boolean} enabled
 * @returns {Promise<{enabled: boolean, success: boolean}>}
 */
export async function setSessionGateEnabled(enabled) {
  const token = localStorage.getItem('google_drive_access_token');
  if (!token) {
    throw new Error('No access token available');
  }

  const response = await fetch('/api/session-config', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ enabled }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Failed to update config' }));
    throw new Error(err.error || 'Failed to update config');
  }
  return response.json();
}

/**
 * Get the session scope restriction data.
 * @returns {{type: string, districts: string[], properties: string[]}|null}
 */
export function getSessionScope() {
  try {
    const scope = localStorage.getItem(SESSION_SCOPE_KEY);
    return scope ? JSON.parse(scope) : null;
  } catch {
    return null;
  }
}

/**
 * Check if a property is allowed by the current session scope.
 * If no scope is set, all properties are allowed (returns true).
 * @param {object} property - Property object with at minimum { id, districtName or district }
 * @returns {boolean}
 */
/**
 * Helper: normalize Vietnamese district name by removing diacritics
 * and mapping special chars (Đ, ư, ơ, etc.) to ASCII equivalents.
 */
function normalizeDistrictName(str) {
  if (!str) return '';
  const vietnameseMap = {
    'đ': 'd', 'Đ': 'd',
    'ă': 'a', 'ắ': 'a', 'ằ': 'a', 'ẳ': 'a', 'ẵ': 'a', 'ặ': 'a',
    'â': 'a', 'ấ': 'a', 'ầ': 'a', 'ẩ': 'a', 'ẫ': 'a', 'ậ': 'a',
    'ê': 'e', 'ế': 'e', 'ề': 'e', 'ể': 'e', 'ễ': 'e', 'ệ': 'e',
    'ô': 'o', 'ố': 'o', 'ồ': 'o', 'ổ': 'o', 'ỗ': 'o', 'ộ': 'o',
    'ơ': 'o', 'ớ': 'o', 'ờ': 'o', 'ở': 'o', 'ỡ': 'o', 'ợ': 'o',
    'ư': 'u', 'ứ': 'u', 'ừ': 'u', 'ử': 'u', 'ữ': 'u', 'ự': 'u',
    'í': 'i', 'ì': 'i', 'ỉ': 'i', 'ĩ': 'i', 'ị': 'i',
    'ú': 'u', 'ù': 'u', 'ủ': 'u', 'ũ': 'u', 'ụ': 'u',
    'é': 'e', 'è': 'e', 'ẻ': 'e', 'ẽ': 'e', 'ẹ': 'e',
    'ó': 'o', 'ò': 'o', 'ỏ': 'o', 'õ': 'o', 'ọ': 'o',
    'á': 'a', 'à': 'a', 'ả': 'a', 'ã': 'a', 'ạ': 'a',
    'ý': 'y', 'ỳ': 'y', 'ỷ': 'y', 'ỹ': 'y', 'ỵ': 'y',
  };
  return str
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .split('').map(ch => vietnameseMap[ch] || ch).join('');
}

/**
 * Check if a property's district matches any of the scope districts.
 */
function isDistrictInScope(propertyDistrictVariants, scopeDistricts) {
  if (!scopeDistricts || scopeDistricts.length === 0) return false;
  const normalizedScope = scopeDistricts.map(normalizeDistrictName);
  const variants = (propertyDistrictVariants || []).filter(Boolean);
  const normalizedProperty = variants.map(normalizeDistrictName);
  return normalizedScope.some(sd =>
    normalizedProperty.some(pd => pd === sd)
  );
}

/**
 * Check if a property is allowed by the current session scope.
 * If no scope is set, all properties are allowed (returns true).
 *
 * Supports scope types:
 *   'all'        - No restriction
 *   'districts'  - Only properties in selected districts
 *   'properties' - Only selected properties by ID
 *   'mixed'      - Properties in selected districts OR selected properties by ID
 *
 * @param {object} property - Property object with at minimum { id, districtName or district }
 * @returns {boolean}
 */
export function isPropertyInSessionScope(property) {
  const scope = getSessionScope();
  if (!scope || scope.type === 'all') {
    return true; // No restriction
  }

  const {
    type,
    districts: scopeDistricts = [],
    properties: scopeProperties = [],
  } = scope;

  // Gather all district variants from the property
  const propertyDistrictVariants = [
    property.districtName,
    property.district,
    property.districtVi,
    property.districtEn,
    property.district_name,
  ].filter(Boolean);

  const propertyId = property.id || property.folderId || '';

  // Check property ID match
  const propertyIdMatch = scopeProperties.includes(propertyId);

  // Check district match
  const districtMatch = isDistrictInScope(propertyDistrictVariants, scopeDistricts);

  if (type === 'districts') {
    return districtMatch;
  }

  if (type === 'properties') {
    return propertyIdMatch;
  }

  // 'mixed' type: property is allowed if its district matches OR its ID matches
  if (type === 'mixed') {
    return districtMatch || propertyIdMatch;
  }

  return true; // Unknown scope type, allow by default
}

/**
 * Filter an array of properties based on the current session scope.
 * If no scope is set, returns the full array unchanged.
 * @param {Array} properties - Array of property objects
 * @returns {Array} Filtered array of properties
 */
export function filterPropertiesBySessionScope(properties) {
  const scope = getSessionScope();
  if (!scope || scope.type === 'all' || !Array.isArray(properties)) {
    return properties;
  }
  return properties.filter(prop => isPropertyInSessionScope(prop));
}

const SessionService = {
  verifySession,
  saveSession,
  getSessionToken,
  getSessionInfo,
  getSessionScope,
  clearSession,
  validateCurrentSession,
  isSessionGateEnabled,
  setSessionGateEnabled,
  isPropertyInSessionScope,
  filterPropertiesBySessionScope,
};

export default SessionService;
